import type { ReadonlyJSONValue } from "@rocicorp/zero";
import {
  type ChatTool,
  toolResultFailed,
  type ToolUseProps,
} from "../components/Chat/ChatTool";
import { Step } from "../components/Step";
import { Text } from "../components/Text";
import type { BashData } from "./Bash";

function parseInput(toolUse: ToolUseProps["toolUse"]) {
  const v = toolUse.input;
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, ReadonlyJSONValue>)
    : null;
}

function str(v: ReadonlyJSONValue | undefined): string | undefined {
  return typeof v === "string" ? v : undefined;
}

function stepFromPath(path: string | undefined): string | undefined {
  if (!path) return undefined;
  const first = path.replace(/^\/+/, "").split("/")[0];
  return first && !first.startsWith(".") ? first : undefined;
}

function CopyStorageFileToStepHeader({
  toolUse,
  result,
  data,
  interrupted,
}: ToolUseProps) {
  const input = parseInput(toolUse);
  const stepName = stepFromPath(str(input?.path));
  const color = stepName
    ? (data as BashData | undefined)?.getStepColor(stepName)
    : undefined;

  return (
    <Step
      color={color ? `var(--${color}-600)` : undefined}
      icon="upload"
      name={stepName}
      done={!!result}
      interrupted={interrupted}
      pastParticiple={str(input?.completedDescription) ?? "Added file"}
      failure={
        result && toolResultFailed(result)
          ? (str(input?.failedDescription) ?? "Failed")
          : undefined
      }
      inProgress={
        str(input?.activeDescription)
          ? `${str(input?.activeDescription)}…`
          : "Adding file…"
      }
    />
  );
}

function CopyStorageFileToStepBody({ toolUse, result }: ToolUseProps) {
  const input = parseInput(toolUse);
  const source = str(input?.source);
  const path = str(input?.path);

  return (
    <Text variant="code-small" color="var(--text-secondary)">
      {path && (
        <pre
          style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {source ? `${source} → ${path}` : path}
        </pre>
      )}
      {result && (
        <pre
          style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {JSON.stringify(result.output, null, 2)}
        </pre>
      )}
    </Text>
  );
}

export const copyStorageFileToStep = (data: BashData): ChatTool => ({
  name: "copyStorageFileToStep",
  Header: CopyStorageFileToStepHeader,
  Body: CopyStorageFileToStepBody,
  data,
});
